import React from 'react'
import { isNull } from 'lodash'
import {
  Events_events_pagination,
  Events_events_events,
  SortDirectionEnum,
  getFormattedDateStartAndEndTime,
  useGenerateNewEventInvoice,
  PaymentMethod,
} from '@modmd/data'
import { Box } from 'components/Layout'
import Datalist, { DataListProps } from 'components/Datalist'
import Pagination from 'components/Pagination'
import Card from 'components/Card'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { DEVICE } from 'theme'
import { EventPaymentLabel } from 'sharedComponents/EventPaymentLabel'
import { isBefore } from 'date-fns'
import Label from 'components/Label'
import { Checkbox } from 'components/Checkbox'
import Button from 'components/Button'
import { checkLaunchPopupError, launchPopup } from 'utils/helpers'
import { useToaster } from 'utils/useToaster'
import { ConfirmationDialog } from 'components/Dialog'
import { Text } from 'components/Typography'

interface ColumnLabels {
  eventId?: string | null
  frequency?: string | null
  date?: string | null
  address?: string | null
  group?: string | null
  name?: string | null
  status?: string | null
  payment?: string | null
  invoiceCheck?: string | null
  invoiceDate?: string | null
}

interface Props {
  tableHeader?: React.ReactElement
  tableFooter?: React.ReactElement
  events: Events_events_events[]
  sorter?: DataListProps['sorter']
  columnLabels?: ColumnLabels
  pagination?: Events_events_pagination
  isCard?: boolean
  isLoading?: boolean
  hasPaginationInFooter?: boolean
  onPageChange?: (page: number) => void
  onSortChange?: (sortBy: string, direction: SortDirectionEnum) => void
  onRowClick?: (data: Events_events_events) => void | null
  pageLength?: number
  listOfInvoices?: Array<string>
  setListOfInvoices?: React.Dispatch<React.SetStateAction<Array<string>>>
  eventRefetch?: () => void
  containInvoiceGenerator?: boolean
}

export const PAGE_LENGTH = 10

const getDefaultColumnLabels = (containInvoiceGenerator = false) => ({
  eventId: 'Event ID',
  frequency: 'Frequency',
  date: 'Date',
  address: 'Location',
  group: 'Group',
  name: 'Name',
  status: 'Status',
  payment: 'Payment',
  invoiceCheck: containInvoiceGenerator ? 'Invoice' : null,
  invoiceDate: null,
})

export const EventSessionDataList: React.VFC<Props> = ({
  tableHeader,
  tableFooter,
  events,
  sorter,
  pagination,
  columnLabels: providedColumnLabels,
  isCard = true,
  isLoading,
  hasPaginationInFooter = false,
  onSortChange,
  onPageChange,
  pageLength = PAGE_LENGTH,
  onRowClick,
  listOfInvoices,
  setListOfInvoices,
  containInvoiceGenerator = false,
  eventRefetch,
}) => {
  const [confirmModal, setConfirmModal] = React.useState(false)
  const [invoiceData, setInvoiceData] = React.useState({
    invoiceDocNumber: '',
    invoiceId: '',
    testSessionId: '',
  })
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const isTablet = useIsMinDevice(DEVICE.TABLET)

  const { setToastMessage } = useToaster()

  const [newInvoice, { loading: isLoadingNewInvoice }] = useGenerateNewEventInvoice()

  const getEventStatus = (isActive: boolean, finishedAt: Date | null, firstDay: Date | null) => {
    if (finishedAt) return 'Finished'
    if (!isActive) return 'Inactive'
    if (firstDay && isBefore(new Date(), firstDay)) return 'Waiting'
    return 'Active'
  }

  const defaultColumnLabels = getDefaultColumnLabels(containInvoiceGenerator)

  const columnLabels = { ...defaultColumnLabels, ...(providedColumnLabels || {}) }
  const numOfColumns = Object.values(columnLabels).filter((label) => label !== null).length
  const rowGridTemplateColumns = React.useMemo(() => `repeat(${numOfColumns}, 1fr)`, [numOfColumns])

  const paginationComp = pagination && pagination.total > pageLength && onPageChange && (
    <Pagination
      firstPage={1}
      lastPage={Math.ceil(pagination.total / pageLength) || 1}
      currentPage={Math.ceil(pagination.from / pageLength) + 1}
      onPageChange={onPageChange}
    />
  )

  const addOrRemoveInvoice = (id: string) => {
    if (setListOfInvoices && listOfInvoices)
      if (listOfInvoices.includes(id))
        setListOfInvoices(listOfInvoices.filter((item) => item !== id))
      else setListOfInvoices([...listOfInvoices, id])
  }

  const showConfirmGenerateInvoice = (invoiceId = '', invoiceDocNumber = '') => {
    const eventsIds = events
      .filter((ts) => ts.Invoice?.invoiceId === invoiceId)
      .map((ts) => ts.id)
      .join(',')
    setInvoiceData({
      invoiceDocNumber,
      invoiceId,
      testSessionId: eventsIds,
    })
    setConfirmModal(true)
  }

  const dismissConfirmGenerateInvoice = () => {
    setConfirmModal(false)
    setInvoiceData({
      invoiceDocNumber: '',
      invoiceId: '',
      testSessionId: '',
    })
  }

  const generateOtherInvoice = async (id: string) => {
    try {
      await newInvoice({
        variables: {
          inputData: id,
        },
      })
      setConfirmModal(false)
      setInvoiceData({
        invoiceDocNumber: '',
        invoiceId: '',
        testSessionId: '',
      })
      if (eventRefetch) {
        void eventRefetch()
      }
    } catch (e) {
      setToastMessage('No Quickbooks connection. Please login', 'danger')
      if (checkLaunchPopupError(String(e).split('Error: ')[1])) {
        launchPopup(String(e).split('Error: ')[1])
      }
    }
  }

  const showEventInvoice = ({ id, Invoice, finishedAt, paymentMethod }: Events_events_events) => {
    if (paymentMethod !== null && paymentMethod !== PaymentMethod.Invoice) {
      return (
        <Label size="l" indicator="red" color="red">
          Not billable
        </Label>
      )
    }
    return Invoice?.invoiceId || !finishedAt ? (
      <>
        {Invoice?.invoiceId && (
          <Button
            appearance="ghost"
            size="small"
            onClick={() =>
              showConfirmGenerateInvoice(
                Invoice?.invoiceId as string,
                Invoice?.invoiceDocNumber as string
              )
            }
            style={{ whiteSpace: 'initial', height: '100%', width: 113 }}
          >
            {Invoice?.invoiceDocNumber
              ? `Invoice NO: ${Invoice?.invoiceDocNumber}`
              : `Invoice ID: ${Invoice?.invoiceId}`}
          </Button>
        )}
        {!Invoice?.invoiceId && (
          <Label size="l" indicator="grayDark">
            Not finished yet
          </Label>
        )}
      </>
    ) : (
      <Checkbox
        checked={listOfInvoices && listOfInvoices.includes(id)}
        onChange={() => addOrRemoveInvoice(id)}
        label=""
        hasBottomContent={false}
      />
    )
  }

  const Wrapper = isCard ? Card : Box

  return (
    <>
      <Wrapper>
        <Datalist
          isLoading={isLoading}
          skeletonProps={{
            columns: numOfColumns,
            rows: pageLength,
          }}
          data={events}
          sorter={sorter}
          onSortChange={onSortChange}
          header={tableHeader}
          footer={hasPaginationInFooter && paginationComp ? paginationComp : tableFooter}
          shouldRenderTableHeader={isDesktop}
          showActionIndicators
          renderTableHeader={
            isDesktop
              ? () => (
                  <Datalist.Row gridTemplateColumns={rowGridTemplateColumns}>
                    {!isNull(columnLabels.eventId) ? (
                      <Datalist.HeaderCell label={columnLabels.eventId} />
                    ) : null}
                    {!isNull(columnLabels.invoiceCheck) ? (
                      <Datalist.HeaderCell label={columnLabels.invoiceCheck} />
                    ) : null}
                    {!isNull(columnLabels.frequency) ? (
                      <Datalist.HeaderCell label={columnLabels.frequency} />
                    ) : null}
                    {!isNull(columnLabels.date) ? (
                      <Datalist.HeaderCell label={columnLabels.date} />
                    ) : null}
                    {!isNull(columnLabels.address) ? (
                      <Datalist.HeaderCell label={columnLabels.address} />
                    ) : null}
                    {!isNull(columnLabels.name) ? (
                      <Datalist.HeaderCell label={columnLabels.name} />
                    ) : null}
                    {!isNull(columnLabels.status) ? (
                      <Datalist.HeaderCell label={columnLabels.status} />
                    ) : null}
                    {!isNull(columnLabels.group) ? (
                      <Datalist.HeaderCell label={columnLabels.group} />
                    ) : null}
                    {!isNull(columnLabels.payment) ? (
                      <Datalist.HeaderCell label={columnLabels.payment} />
                    ) : null}
                    {!isNull(columnLabels.invoiceDate) ? (
                      <Datalist.HeaderCell label={columnLabels?.invoiceDate} />
                    ) : null}
                  </Datalist.Row>
                )
              : undefined
          }
          renderTableRow={(data) => {
            const {
              id,
              frequency,
              Address,
              Group,
              name,
              paymentMethod,
              EventDays,
              isActive,
              finishedAt,
              Invoice,
            } = data
            return (
              <Datalist.Row
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: rowGridTemplateColumns,
                }}
                {...(!isTablet ? { alignItems: 'start' } : {})}
                {...(!onRowClick || containInvoiceGenerator
                  ? {}
                  : { onClick: () => onRowClick(data) })}
              >
                {!isNull(columnLabels.eventId) ? (
                  <Datalist.Cell label={columnLabels.eventId}>{id}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.invoiceCheck) ? (
                  <Datalist.Cell label={columnLabels.invoiceCheck}>
                    {showEventInvoice(data)}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.frequency) ? (
                  <Datalist.Cell label={columnLabels.frequency}>{frequency}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.date) ? (
                  <Datalist.Cell label={columnLabels.date}>
                    {!EventDays?.length && '-'}
                    {EventDays?.map((day) => (
                      <Box>
                        {getFormattedDateStartAndEndTime(new Date(day.startTime), day.timeRange)}
                      </Box>
                    ))}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.address) ? (
                  <Datalist.Cell label={columnLabels.address}>
                    {Address
                      ? `${Address?.street}, ${Address?.city}, ${Address?.state || ''}`
                      : 'No address'}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.name) ? (
                  <Datalist.Cell label={columnLabels.name}>{name}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.status) ? (
                  <Datalist.Cell label={columnLabels.status}>
                    {getEventStatus(
                      isActive,
                      finishedAt ? new Date(finishedAt) : null,
                      EventDays && EventDays[0] ? new Date(EventDays[0].startTime) : null
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.group) ? (
                  <Datalist.Cell label={columnLabels.group}>{Group?.name}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.payment) ? (
                  <Datalist.Cell label={columnLabels.payment}>
                    <EventPaymentLabel
                      paymentStatus={paymentMethod}
                      invoiceDocNumber={Invoice?.invoiceDocNumber}
                    />
                  </Datalist.Cell>
                ) : null}
              </Datalist.Row>
            )
          }}
        />
      </Wrapper>
      {!hasPaginationInFooter && paginationComp && (
        <Pagination.Wrapper>{paginationComp}</Pagination.Wrapper>
      )}
      <ConfirmationDialog
        title="Generate invoice"
        isOpen={confirmModal}
        onDismiss={dismissConfirmGenerateInvoice}
        actions={
          <>
            <Button onClick={dismissConfirmGenerateInvoice} colorVariant="secondary">
              Cancel
            </Button>
            <Button
              onClick={() => generateOtherInvoice(invoiceData.invoiceId)}
              isFetching={isLoadingNewInvoice}
            >
              Cancel and Generate
            </Button>
          </>
        }
      >
        <>
          <Text>Do you want to cancel this invoice and generate a new one? </Text>
          {invoiceData.testSessionId && invoiceData.invoiceId && (
            <Text>
              <strong>Event:</strong> {invoiceData.testSessionId}{' '}
              {invoiceData.invoiceDocNumber ? (
                <>
                  <strong>Invoice NO:</strong> {invoiceData.invoiceDocNumber}
                </>
              ) : (
                <>
                  <strong>Invoice ID:</strong> {invoiceData.invoiceId}
                </>
              )}
            </Text>
          )}
        </>
      </ConfirmationDialog>
    </>
  )
}
